import React, {
	Suspense,
	createContext,
	lazy,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Route,
	Navigate,
	createBrowserRouter,
	RouterProvider,
	createRoutesFromElements,
	Outlet,
} from 'react-router-dom';
import { FeaturesReady, useFeature } from '@growthbook/growthbook-react';
import { growthbookFeatures } from '@app/constants/growthbook';

import Login from '../containers/LoginScreen';
import AuthCallback from '../containers/AuthCallback';
import Unsubscribe from '../containers/Unsubscribe';

import PrivateRoute from './PrivateRoute';
import { PageRouteEnum, ActivityRoute } from '../constants/pages';
import { LoaderContainer } from './PrivateRoute.styles';
import lazyRetry from '@app/utils/lazyRetry';

import { WorkspacePlansEnum } from '@app/constants/modules/workspace';
import NavigationBar from '@app/components/modules/navbar/NavigationBar/NavigationBar';
import { BrowserTitleHandler, Segment } from '@app/services';
import ErrorBoundary from '@app/components/modules/workspace/ErrorBoundary';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '@app/redux/types';
import { grantAccess, setAuthProviderInfo } from '@app/redux/auth/action';
import { getWorkspaceInfo } from '@app/redux/workspace/action';
import TokenStorage from '@app/services/Auth/TokenStorage';
import { useAuth } from '@app/services/Auth/useAuth';
import MobileAppRedirect from '@app/containers/MobileAppRedirect/MobileAppRedirect';
import { SpinningLoader } from '@nicheinc/campus-ds';
import styled from 'styled-components';
import { checkWorkspaceIsChatbotOnly } from '@app/utils/modules/workspace';
import checkIsGKAdmin from '@app/utils/checkIsGKAdmin';
import { useAllowedCampaignBuilders } from '@app/hooks/modules/campaign/useAllowedCampaignBuilders';
import { useIsAdvanceOnly } from '@app/hooks/modules/workspace/useIsAdvanceOnly';

const Home = React.lazy(() => lazyRetry(() => import('../containers/Home')));

const SettingsV2 = React.lazy(() =>
	lazyRetry(() => import('../containers/SettingsV2')),
);

const CampaignReport = React.lazy(() =>
	lazyRetry(() => import('../containers/Report')),
);

const FunnelReport = React.lazy(() =>
	lazyRetry(() => import('../containers/Report/FunnelReport/FunnelReport')),
);

const CampaignsList = React.lazy(() =>
	lazyRetry(() => import('../containers/Campaigns')),
);

const AudiencesV2 = lazy(() =>
	lazyRetry(() => import('../containers/AudiencesV2')),
);

const ChangeWorkspace = lazy(() =>
	lazyRetry(() => import('../containers/ChangeWorkspace')),
);

const CampaignCreationV2 = lazy(() =>
	lazyRetry(() => import('../containers/CampaignCreationV2')),
);

const CampaignCreationV3 = lazy(() =>
	lazyRetry(() => import('../containers/CampaignCreationV3')),
);

const VideosV2 = lazy(() => lazyRetry(() => import('../containers/VideosV2')));

const Activity = lazy(() => lazyRetry(() => import('../containers/Activity')));

const Recorders = lazy(() =>
	lazyRetry(() => import('../containers/Recorders')),
);

const Inbox = lazy(() => lazyRetry(() => import('../containers/Inbox')));

const SuccessPlan = lazy(() =>
	lazyRetry(() => import('../containers/SuccessPlan')),
);

const Chatbot = lazy(() => lazyRetry(() => import('../containers/Chatbot')));

const ChatbotSettings = lazy(() =>
	lazyRetry(() => import('../containers/Chatbot/ChatBotSettings')),
);

const ChatbotList = lazy(() =>
	lazyRetry(
		() => import('../containers/Chatbot/ChatBotsListContainer/ChatbotsList'),
	),
);

const VideoFolder = lazy(() =>
	lazyRetry(() => import('../containers/VideosV2/containers/VideoFolder')),
);

const Teams = lazy(() => lazyRetry(() => import('@app/containers/Teams')));

const NewTeam = lazy(() =>
	lazyRetry(() => import('@app/containers/Teams/NewTeam')),
);

const TeamProfile = lazy(() =>
	lazyRetry(() => import('@app/containers/Teams/TeamProfile')),
);

const TeamSettings = lazy(() =>
	lazyRetry(() => import('@app/containers/Teams/TeamSettings')),
);

const Inquiries = lazy(() =>
	lazyRetry(() => import('@app/containers/Inquiries')),
);

export const Layout = styled.div`
	position: relative;
	display: flex;
	height: 100vh;
	max-height: 100%;
`;

/**
 * Runtime values the route tree reacts to. They reach route elements through
 * context instead of being baked into the router, so the router is created
 * once: rebuilding it leaves the old instance listening and causes a garbage route tree.
 */
type RouteFlags = {
	isShowingNavBar: boolean;
	isInquiryFormBuilderOn: boolean;
	isAdvanceOnlyWorkspace: boolean;
	isChatbotOnlyWorkspace: boolean;
	isNicheAdmin: boolean;
	hasLite: boolean;
	hasLegacy: boolean;
	isReady: boolean;
	/** Workspace payload AND the Redux auth user have loaded. */
	isAccessReady: boolean;
};

const RouteFlagsContext = createContext<RouteFlags | null>(null);

const useRouteFlags = () => {
	const flags = useContext(RouteFlagsContext);
	if (!flags) {
		throw new Error('useRouteFlags must be used within <Routers />');
	}
	return flags;
};

type GatedRouteProps = Omit<
	React.ComponentProps<typeof PrivateRoute>,
	'disabledRoute'
> & {
	/** Derives PrivateRoute's `disabledRoute` from the live route flags. */
	disabledWhen: (flags: RouteFlags) => boolean;
};

function GatedRoute({ disabledWhen, ...props }: GatedRouteProps) {
	const flags = useRouteFlags();
	return <PrivateRoute {...props} disabledRoute={disabledWhen(flags)} />;
}

function InquiryPage() {
	const { isInquiryFormBuilderOn } = useRouteFlags();
	return isInquiryFormBuilderOn ? <Inquiries /> : <Navigate to="/" />;
}

function RouteLayout() {
	const { isShowingNavBar } = useRouteFlags();

	return (
		<>
			<BrowserTitleHandler />
			<Segment />

			<Layout>
				{isShowingNavBar && <NavigationBar />}
				<Suspense
					fallback={
						<LoaderContainer>
							<SpinningLoader />
						</LoaderContainer>
					}
				>
					<Outlet />
				</Suspense>
			</Layout>
		</>
	);
}

type getRouterProps = {
	setIsShowingNavBar: (val: boolean) => void;
};
const getRouter = ({ setIsShowingNavBar }: getRouterProps) => {
	return createBrowserRouter(
		createRoutesFromElements(
			<Route
				errorElement={
					<ErrorBoundary handleReset={() => window.location.reload()} />
				}
				element={<RouteLayout />}
			>
				<Route path="/" element={<Login />} />

				<Route path="/entity/:entityUUID" element={<Login />} />

				<Route
					path={`/${PageRouteEnum.changingWorkspace}/redirect/entity/:entityUUID`}
					element={<ChangeWorkspace />}
				/>

				<Route
					path={`/${PageRouteEnum.mobileAppRedirect}`}
					element={<MobileAppRedirect />}
				/>

				<Route
					path={`/${PageRouteEnum.callback}/*`}
					element={<AuthCallback />}
				/>

				<Route
					path={`/${PageRouteEnum.unsubscribeEmail}`}
					element={<Unsubscribe />}
				/>

				<Route
					path={`/${PageRouteEnum.changingWorkspace}/`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar}>
							<ChangeWorkspace />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.changingWorkspaceFallback}/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar}>
							<ChangeWorkspace />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.inbox}/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar}>
							<Inbox />
						</PrivateRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.inbox}/:teamId/*`}
					element={
						<PrivateRoute
							setIsShowingNavBar={setIsShowingNavBar}
							pageWrapperOverflowHidden
						>
							<Inbox />
						</PrivateRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.inbox}/:teamId/:conversationId/*`}
					element={
						<PrivateRoute
							setIsShowingNavBar={setIsShowingNavBar}
							pageWrapperOverflowHidden
						>
							<Inbox />
						</PrivateRoute>
					}
				/>

				{/** Teams */}

				<Route
					path={`/${PageRouteEnum.teams}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.SMS,
								WorkspacePlansEnum.EMBEDDED_REELS,
								WorkspacePlansEnum.CHATBOT,
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
							]}
							disabledWhen={(f) =>
								(f.isAdvanceOnlyWorkspace && !f.isNicheAdmin) ||
								f.isChatbotOnlyWorkspace
							}
						>
							<Teams />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.teams}/${PageRouteEnum.newTeam}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.SMS,
								WorkspacePlansEnum.EMBEDDED_REELS,
								WorkspacePlansEnum.CHATBOT,
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
							]}
							disabledWhen={(f) =>
								f.isAccessReady && (!f.isNicheAdmin || f.isChatbotOnlyWorkspace)
							}
						>
							<NewTeam />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.teams}/:teamId/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.SMS,
								WorkspacePlansEnum.EMBEDDED_REELS,
								WorkspacePlansEnum.CHATBOT,
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
							]}
							disabledWhen={(f) =>
								(f.isAdvanceOnlyWorkspace && !f.isNicheAdmin) ||
								f.isChatbotOnlyWorkspace
							}
						>
							<TeamProfile />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.teams}/:teamId/settings/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.SMS,
								WorkspacePlansEnum.EMBEDDED_REELS,
								WorkspacePlansEnum.CHATBOT,
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
							]}
							disabledWhen={(f) =>
								(f.isAdvanceOnlyWorkspace && !f.isNicheAdmin) ||
								f.isChatbotOnlyWorkspace
							}
						>
							<TeamSettings />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.recorders}/*`}
					element={
						<PrivateRoute
							setIsShowingNavBar={setIsShowingNavBar}
							workspacePlans={[WorkspacePlansEnum.CHATBOT]}
							adminRoute
						>
							<Recorders />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.home}/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar}>
							<Home />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.campaigns}/*`}
					element={
						<PrivateRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							pageWrapperOverflowHidden
						>
							<CampaignsList />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.campaign}/:campaignId/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar} adminRoute>
							<FeaturesReady
								timeout={500}
								fallback={
									<LoaderContainer>
										<SpinningLoader />
									</LoaderContainer>
								}
							>
								<CampaignReport />
							</FeaturesReady>
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.funnel}/:funnelId/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar} adminRoute>
							<FeaturesReady
								timeout={500}
								fallback={
									<LoaderContainer>
										<SpinningLoader />
									</LoaderContainer>
								}
							>
								<FunnelReport />
							</FeaturesReady>
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.audiences}/*`}
					element={
						<PrivateRoute
							adminRoute
							setIsShowingNavBar={setIsShowingNavBar}
							pageWrapperOverflowHidden
						>
							<AudiencesV2 />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.settings}/*`}
					element={
						<PrivateRoute
							pageWrapperOverflowHidden
							setIsShowingNavBar={setIsShowingNavBar}
						>
							<SettingsV2 setIsShowingNavBar={setIsShowingNavBar} />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.campaignCreationV2}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
								WorkspacePlansEnum.EMBEDDED_REELS,
							]}
							disabledWhen={(f) => f.isReady && !f.hasLegacy}
						>
							<CampaignCreationV2 />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.campaignCreationV3}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.ADVANCE_2_0,
								WorkspacePlansEnum.ADVANCE,
							]}
							disabledWhen={(f) => f.isReady && !f.hasLite}
						>
							<CampaignCreationV3 />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.videos}/*`}
					element={
						<PrivateRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
								WorkspacePlansEnum.EMBEDDED_REELS,
							]}
						>
							<VideosV2 />
						</PrivateRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.videos}/folder/:folderId/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
								WorkspacePlansEnum.EMBEDDED_REELS,
							]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<VideoFolder />
						</GatedRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.activity}`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
								WorkspacePlansEnum.SMS,
							]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<Navigate to={ActivityRoute.SCHEDULED} />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.activity}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							pageWrapperOverflowHidden
							workspacePlans={[
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.REELS,
								WorkspacePlansEnum.SMS,
							]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<Activity />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.successPlan}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<SuccessPlan />
						</GatedRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.chatbot}/*`}
					element={
						<PrivateRoute setIsShowingNavBar={setIsShowingNavBar} adminRoute>
							<Chatbot />
						</PrivateRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.chatbot}/list/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[WorkspacePlansEnum.CHATBOT]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<ChatbotList />
						</GatedRoute>
					}
				/>
				<Route
					path={`/${PageRouteEnum.chatbot}/:chatbotId/chatbotSettings/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[WorkspacePlansEnum.CHATBOT]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<ChatbotSettings />
						</GatedRoute>
					}
				/>

				<Route
					path={`/${PageRouteEnum.inquiries}/*`}
					element={
						<GatedRoute
							setIsShowingNavBar={setIsShowingNavBar}
							adminRoute
							workspacePlans={[
								WorkspacePlansEnum.CHATBOT,
								WorkspacePlansEnum.VIDEO_MESSAGING,
								WorkspacePlansEnum.EMBEDDED_REELS,
							]}
							disabledWhen={(f) => f.isAdvanceOnlyWorkspace}
						>
							<InquiryPage />
						</GatedRoute>
					}
				/>

				<Route path="*" element={<Navigate to="/" />} />
			</Route>,
		),
	);
};

export default function Routers(): React.ReactElement {
	const dispatch = useAppDispatch();

	const isChatbotLeadFormsOn = useFeature(
		growthbookFeatures.CHATBOT_LEAD_FORMS,
	).on;

	const isCampaignLeadFormsOn = useFeature(
		growthbookFeatures.CAMPAIGN_LEAD_FORMS,
	).on;

	const { hasLite, hasLegacy, isReady } = useAllowedCampaignBuilders();

	const isInquiryFormBuilderOn = isChatbotLeadFormsOn || isCampaignLeadFormsOn;

	const [isShowingNavBar, setIsShowingNavBar] = useState<boolean>(false);

	const { workspaceInfo, loading: workspaceLoading } = useSelector(
		({ workspace: { workspaceInfo, loading } }: RootState) => ({
			workspaceInfo,
			loading,
		}),
	);
	const { authProvider, user: accessUser, propelAccessToken } = useAuth();

	const isAdvanceOnlyWorkspace = useIsAdvanceOnly();
	const isChatbotOnlyWorkspace = checkWorkspaceIsChatbotOnly(workspaceInfo);
	const isNicheAdmin = checkIsGKAdmin(accessUser?.email);

	// `isNicheAdmin` reads the Redux auth user, which `grantAccess` only populates
	// after the workspace payload lands, so it reads false for everyone on the
	// first render. Routes that deny access when it's false must wait for this.
	const isAccessReady = isReady && !!accessUser?.email;

	const routeFlags = useMemo<RouteFlags>(
		() => ({
			isShowingNavBar,
			isInquiryFormBuilderOn,
			isAdvanceOnlyWorkspace,
			isChatbotOnlyWorkspace,
			isNicheAdmin,
			hasLite,
			hasLegacy,
			isReady,
			isAccessReady,
		}),
		[
			isShowingNavBar,
			isInquiryFormBuilderOn,
			isAdvanceOnlyWorkspace,
			isChatbotOnlyWorkspace,
			isNicheAdmin,
			hasLite,
			hasLegacy,
			isReady,
			isAccessReady,
		],
	);

	// Create the router exactly once, anything that changes at runtime goes
	// through `routeFlags` (see RouteFlags). `setIsShowingNavBar` is a stable
	// state setter, so capturing it here never goes stale.
	const [router] = useState(() => getRouter({ setIsShowingNavBar }));

	const hasIdentityToken = !!TokenStorage.getIdentity();

	const isLoggedIn = useMemo(() => {
		return authProvider?.isAuthenticated && hasIdentityToken;
	}, [authProvider?.isAuthenticated, hasIdentityToken]);

	/* Logic to get workspace info when the page is refreshed and when the user is logged in on auth provider
	check for workspace id and if not then get workspace info to grant access and set auth client in redux
	*/
	useEffect(() => {
		if (!workspaceInfo?._id && isLoggedIn && !workspaceLoading) {
			dispatch(getWorkspaceInfo());
			dispatch(
				setAuthProviderInfo({
					logout: authProvider?.logout,
					loginWithRedirect: authProvider?.loginWithRedirect,
					user: authProvider?.user,
				}),
			);
		}
	}, [isLoggedIn, authProvider, workspaceInfo?._id, workspaceLoading]);

	/*
	 |
	 | 
	 |
	 |
	\ / 
	*/

	/* Logic to get the current logged user when the page is refreshed
	 and when the user is logged in - check for access user in redux, check for access token and workspace id
	to grant access if not already granted 
	*/
	/*
		Need to check for niche auth provider so that get Redux auth user gets set in this case as well)
	*/
	useEffect(() => {
		if (
			!accessUser &&
			(!!TokenStorage.getAccessToken() || authProvider?.name === 'niche') &&
			authProvider?.user?.email &&
			workspaceInfo?._id
		) {
			dispatch(
				grantAccess(workspaceInfo?._id as string, authProvider?.user?.email),
			);
		}
	}, [accessUser, workspaceInfo?._id, authProvider?.user?.email]);

	/* Logic to set the access token in storage when propel token refresh*/
	useEffect(() => {
		if (authProvider?.name === 'propelAuth' && propelAccessToken) {
			TokenStorage.setAccessToken(propelAccessToken);
		}
	}, [propelAccessToken, authProvider?.name]);

	return (
		<Suspense
			fallback={
				<LoaderContainer>
					<SpinningLoader />
				</LoaderContainer>
			}
		>
			<RouteFlagsContext.Provider value={routeFlags}>
				<RouterProvider
					future={
						{ v7_startTransition: false, v7_relativeSplatPath: false } as any
					}
					router={router}
				/>
			</RouteFlagsContext.Provider>
		</Suspense>
	);
}
